/* eslint-disable react-hooks/exhaustive-deps */
import { FunctionComponent as F, useEffect, useState, Fragment } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from 'react-redux';
import { useAppDispatch } from "../../core/store/hooks";
import { deleteProductAsync } from "../../core/store/products/products.thunks";
import { IState } from "../../core/store/store";
import {
  Table as MaUTable,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Box,
  Stack,
  Typography,
  Link } from '@mui/material';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { LOGIN, NEW, PRODUCTS } from "../../core/router/paths";
import LinkRow from "../UI/Table/LinkRow";
import { handleToggleSortValues } from "../../utils/table";
import HeadingSort from "../UI/Table/HeadingSort";
import { alphabeticalSort } from "../../utils/string";
import TextTooltip from "../UI/Tooltip/TextTooltip";
import { SortDirection, SortFn } from "../../core/types/component";



interface Props
{
  handleProductClick: ((productId: number) => void) | (() => void);
  addNew?: boolean
}

interface Value
{
  productId: number,
  brandName: string,
  productName: string,
  amount: string,
  remove: boolean,
}

interface Params
{
  protocolId?: string
}

const ProductsTable: F<Props> = props =>
{
  const navigate = useNavigate();
  const appDispatch = useAppDispatch();

  const products = useSelector((state: IState) => state.products.products);
  const user = useSelector((state: IState) => state.user);


  const displayData = products?.length ? products
    .map(row => ({
      ...row,
      productId: row.productId,
      brandName: row.brandName,
      productName: row.productName,
      amount: row.amount.toFixed(0) + " " + row.amountUnit,
      remove: Boolean(row.userId) }))
    .filter(p => p.deliverableListingsForUser || p.userId === user.userId) : [];

  const standardDisplayColumns = [ {
    Header: 'Name', accessor: 'productName' }, {
    Header: 'Brand', accessor: 'brandName' } ];
  const displayColumns = products?.every(product => !product.userId) ?
    standardDisplayColumns :
    standardDisplayColumns.concat({ Header: '', accessor: 'remove'});

  const [headings, setHeadings] = useState(displayColumns);
  const [values, setValues] = useState<Value[]>(displayData);

  // Update columns and records when search results arrive
  useEffect(() =>
  {
    setHeadings(displayColumns);
    setValues(displayData);
  }, [products]);

  // Sorting
  const [productNameSort, setProductNameSort] = useState<SortDirection | null>(SortDirection.alphabetical);
  const [brandNameSort, setBrandNameSort] = useState<SortDirection | null>(null);
  const allSetSorts = [setProductNameSort, setBrandNameSort];

  const handleToggleSort = (
    key: string,
    sort: SortDirection | null,
    setSort: (value: React.SetStateAction<SortDirection | null>) => void,
    sortFn?: SortFn) =>
    handleToggleSortValues<Value>({
      values,
      setValues,
      key,
      sortDirection: sort,
      setSortDirection: setSort,
      unsetSortDirections: allSetSorts,
      sortFn});

  const handleRemove = async(rowIndex: number) =>
  {
    if(!products)
      return;

    await appDispatch(deleteProductAsync(products[rowIndex].productId));
  };

  return (
    <MaUTable size='small' id='ProductsTable'>
      <TableHead>
        <TableRow>
          <TableCell key={0}>
            <HeadingSort
              headingText={headings[0].Header}
              sort={productNameSort}
              handleToggleSort={handleToggleSort(
                'productName',
                productNameSort,
                setProductNameSort,
                alphabeticalSort)} />
          </TableCell>
          <TableCell key={1}>
            <HeadingSort
              headingText={headings[1].Header}
              sort={brandNameSort}
              handleToggleSort={handleToggleSort(
                'brandName',
                brandNameSort,
                setBrandNameSort,
                alphabeticalSort)} />
          </TableCell>
          {products?.filter(p => p.userId).length ? <TableCell key={2}/> : <></>}
        </TableRow>
      </TableHead>
      <TableBody>
        {values.map((row, rowIndex) =>
        {
          return (
            <TableRow key={rowIndex}>
              {headings.map(heading => heading.accessor).map((accessor, i) =>
              {
                if(!products)
                  return <TableCell/>;

                switch(accessor)
                {
                case 'productName':
                  return(
                    <TableCell key={i}>
                      <Stack direction='row'>
                        <Link
                          variant='body2'
                          underline='hover'
                          onClick={() =>
                          {
                            const productId = displayData.filter(dataRow =>
                              dataRow.brandName === row.brandName &&
                              dataRow.productName === row.productName &&
                              dataRow.amount === row.amount)[0].productId;

                            if(productId)
                              props.handleProductClick(productId);
                          }}
                          style={{ cursor: 'pointer' }} >
                          {row[accessor]}
                        </Link>
                        <Box sx={{ width: 10 }}/>
                        <Typography variant='body2' style={{ color: 'gray' }}>
                          {"(" + row.amount + ")"}
                        </Typography>
                      </Stack>
                    </TableCell>);
                case 'remove':
                  return(
                    <TableCell key={i} style={{ width: '10px' }}>
                      {row[accessor] ?
                        <TextTooltip lines={["Delete product"]}>
                          <RemoveCircleIcon
                            onClick={() => handleRemove(rowIndex)}
                            style={{ cursor: 'pointer', color: 'red' }}/>
                        </TextTooltip> :
                        <Fragment/>}
                    </TableCell>);
                case 'brandName':
                  return <TableCell key={i}>{row[accessor]}</TableCell>;
                }
              })}
            </TableRow>);
        })}
        {(props.addNew !== false) ?
          (user.username ?
            <LinkRow
              linkId='Add'
              colSpan={displayColumns.length}
              text={"Add a new product"}
              handleClick={() => navigate(`./${NEW}`, { relative: 'path' })}/> :
            <LinkRow
              colSpan={displayColumns.length}
              text={"Log in to add a new product"}
              handleClick={() => navigate(LOGIN)}/>) :
          <></>}

      </TableBody>
    </MaUTable>);
};

export default ProductsTable;
